import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  Dispatch,
} from '@reduxjs/toolkit';
import { createResetAction } from '../../utils';
import {
  createStaffContact,
  deleteStaffContact,
  getStaffContacts,
  updateStaffContact,
} from '../services/staff-contacts/api';
import {
  CreateStaffContactPayload,
  DeleteStaffContactPayload,
  FetchStaffContactsPayload,
  isStaffContactGroupedListData,
  mergeStaffContactGroups,
  StaffContactListResult,
  StaffContactRecord,
  StaffContactSortField,
  UpdateStaffContactPayload,
} from '../services/staff-contacts/types';
import { initialState } from './interfaces';
import { RootState } from '../store';
import { setShowAlert } from '../utilts/utilsSlice';
import { TOAST_TYPE } from '../../utils/constantTypes';
import type { BackendPaginationMeta } from 'src/components/ServerPaginatedTable/types';
import { DEFAULT_PAGINATION_META } from 'src/components/ServerPaginatedTable/constants';

const showSuccessAlert = (dispatch: Dispatch, message: string) => {
  dispatch(
    setShowAlert({
      show: true,
      message,
      type: TOAST_TYPE.SUCCESS,
    }),
  );
};

const resolveListQuery = (state: RootState['staffContacts']) => {
  const rawOffset = state.pagination.offset;
  const rawLimit = state.pagination.limit;
  const offset =
    Number.isFinite(rawOffset) && rawOffset >= 0 ? Math.floor(rawOffset) : 0;
  const limit =
    Number.isFinite(rawLimit) && rawLimit > 0
      ? Math.min(100, Math.floor(rawLimit))
      : 10;

  return {
    offset,
    limit,
    sortBy: state.sortBy,
    sortOrder: state.sortDirection.toUpperCase() as 'ASC' | 'DESC',
  };
};

const normalizePagination = (
  pagination: BackendPaginationMeta | undefined,
  fallbackOffset: number,
  fallbackLimit: number,
): BackendPaginationMeta => {
  if (!pagination) {
    return {
      ...DEFAULT_PAGINATION_META,
      offset: fallbackOffset,
      limit: fallbackLimit,
    };
  }
  return pagination;
};

export const fetchStaffContactsAsync = createAsyncThunk(
  'staffContacts/fetchAll',
  async (payload: FetchStaffContactsPayload, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = (getState() as RootState).staffContacts;
      const query = payload.query ?? resolveListQuery(state);
      const response = await getStaffContacts(payload, query, { dispatch });
      if (response.statusCode === 200) {
        const contacts = isStaffContactGroupedListData(response.data)
          ? mergeStaffContactGroups(response.data)
          : [];
        const pagination = normalizePagination(
          response.pagination,
          query.offset,
          query.limit,
        );
        return { contacts, pagination } as StaffContactListResult;
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

export const createStaffContactAsync = createAsyncThunk(
  'staffContacts/create',
  async (payload: CreateStaffContactPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await createStaffContact(payload, { dispatch });
      if (response.statusCode === 201) {
        showSuccessAlert(dispatch, response.msg);
        return response.data as StaffContactRecord;
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

export const updateStaffContactAsync = createAsyncThunk(
  'staffContacts/update',
  async (payload: UpdateStaffContactPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateStaffContact(payload, { dispatch });
      if (response.statusCode === 200) {
        showSuccessAlert(dispatch, response.msg);
        return response.data as StaffContactRecord;
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

export const deleteStaffContactAsync = createAsyncThunk(
  'staffContacts/delete',
  async (payload: DeleteStaffContactPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteStaffContact(payload, { dispatch });
      if (response.statusCode === 200) {
        showSuccessAlert(dispatch, response.msg);
        return payload.id;
      }
      return rejectWithValue(response.msg);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong';
      return rejectWithValue(message);
    }
  },
);

const setSaving = (state: typeof initialState, saving: boolean) => {
  state.saving = saving;
};

const staffContactsSlice = createSlice({
  name: 'staffContacts',
  initialState,
  reducers: {
    resetStaffContacts: createResetAction(initialState),
    setPagination: (
      state,
      action: PayloadAction<{ offset: number; limit: number }>,
    ) => {
      state.pagination = {
        ...state.pagination,
        offset: action.payload.offset,
        limit: action.payload.limit,
      };
    },
    setSortBy: (state, action: PayloadAction<StaffContactSortField>) => {
      state.sortBy = action.payload;
    },
    setSortDirection: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortDirection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffContactsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchStaffContactsAsync.fulfilled,
        (state, action: PayloadAction<StaffContactListResult>) => {
          state.loading = false;
          state.hasLoaded = true;
          state.contacts = action.payload.contacts;
          state.pagination = action.payload.pagination;
        },
      )
      .addCase(fetchStaffContactsAsync.rejected, (state, action) => {
        state.loading = false;
        state.hasLoaded = true;
        state.error = (action.payload as string) || action.error.message || null;
      })
      .addCase(createStaffContactAsync.pending, (state) => {
        setSaving(state, true);
      })
      .addCase(createStaffContactAsync.fulfilled, (state) => {
        setSaving(state, false);
      })
      .addCase(createStaffContactAsync.rejected, (state) => {
        setSaving(state, false);
      })
      .addCase(updateStaffContactAsync.pending, (state) => {
        setSaving(state, true);
      })
      .addCase(updateStaffContactAsync.fulfilled, (state) => {
        setSaving(state, false);
      })
      .addCase(updateStaffContactAsync.rejected, (state) => {
        setSaving(state, false);
      })
      .addCase(deleteStaffContactAsync.pending, (state) => {
        setSaving(state, true);
      })
      .addCase(deleteStaffContactAsync.fulfilled, (state) => {
        setSaving(state, false);
      })
      .addCase(deleteStaffContactAsync.rejected, (state) => {
        setSaving(state, false);
      });
  },
});

export const {
  resetStaffContacts,
  setPagination,
  setSortBy,
  setSortDirection,
} = staffContactsSlice.actions;
export default staffContactsSlice.reducer;

export const selectStaffContacts = (state: RootState) =>
  state.staffContacts.contacts;
export const selectStaffContactsLoading = (state: RootState) =>
  state.staffContacts.loading;
export const selectStaffContactsHasLoaded = (state: RootState) =>
  state.staffContacts.hasLoaded;
export const selectStaffContactsSaving = (state: RootState) =>
  state.staffContacts.saving;
export const selectStaffContactsPagination = (state: RootState) =>
  state.staffContacts.pagination;
export const selectStaffContactsSortBy = (state: RootState) =>
  state.staffContacts.sortBy;
export const selectStaffContactsSortDirection = (state: RootState) =>
  state.staffContacts.sortDirection;
